import { WorkOrderExternalSnapshot } from '@myapp/contract'

/**
 * Returns a short location label for calendar blocks — just the city/district (Ort).
 *
 * gebaeude = "Nr 5, Hauptstraße, Berlin Mitte"  → "Berlin Mitte"
 * veAdresse = "Nr 123, Bezeichnung, Hauptstraße 5, 12345 Berlin" → "Berlin" (PLZ stripped)
 *
 * Internal fallback for `workOrderAddress` below — `gebaeude`/`veAdresse` are
 * Wirtschaftseinheit descriptions, not the address a technician drives to, so this stays
 * a last resort rather than the primary source.
 */
function shortLocation(ext: WorkOrderExternalSnapshot | null | undefined): string | null {
  if (!ext) return null

  if (ext.gebaeude) {
    const parts = ext.gebaeude.split(', ')
    return parts[0] || null
  }

  if (ext.veAdresse) {
    const parts = ext.veAdresse.split(', ')
    const last = parts[parts.length - 1] ?? ''
    // strip leading PLZ (5 digits + space), e.g. "12345 Berlin" → "Berlin"
    return last.replace(/^\d{5}\s*/, '') || null
  }

  return null
}

/**
 * The short address every calendar block, board chip and banner shows.
 *
 * `hauseingang` (Immotion `BezeichnungHaus`) is street + house number of the actual
 * entrance — the only field naming where a technician drives to. `gebaeude`/`veAdresse`
 * are Wirtschaftseinheit descriptions and were the wrong field shown here before (client
 * report: "Adressfeld der Kalenderübersicht falsch verknüpft").
 */
export function workOrderAddress(ext: WorkOrderExternalSnapshot | null | undefined): string | null {
  if (!ext) return null
  return ext.hauseingang || shortLocation(ext)
}

/** The full address line for detail surfaces — Hauseingang first, then the rest. */
export function fullAddress(ext: WorkOrderExternalSnapshot | null | undefined): string {
  return [ext?.hauseingang, ext?.gebaeude, ext?.veAdresse].filter(Boolean).join(', ') || '—'
}
